<!--
    AssetManagerMediaItem

    DESCRIPTION
    A single media item rendered by the AssetManagerMedia component
-->

<template>
    <div class="overflow-hidden rounded-md bg-white p-2 border border-gray-200 flex flex-row items-center">
        <button class="inline-flex items-center justify-center h-10 w-6 mr-2 rounded-sm bg-white text-gray-400 flex-grow-0"><DragDropIcon class="h-6 w-6" /></button>
        <img :src="media.url" class="h-auto w-[140px]" />
        <div class="flex-grow-1 w-full flex items-center justify-between px-4">
            <div>
                <h3 class="text-sm font-medium text-gray-900 mb-1" v-html="media.file_name"></h3>
                <button v-on:click="makePrimary" v-if="media.type == 'image' && !form.media.fields.primary.value" type="button"  class="inline-flex items-center gap-x-1.5 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600 cursor-pointer">
                    <StarSolidIcon class="h-2.5 w-2.5" />
                    Make Primary Image
                </button>
                <span v-if="media.type == 'image' && form.media.fields.primary.value"  class="inline-flex items-center gap-x-1.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                    <StarSolidIcon class="h-2.5 w-2.5" />
                    Primary Image
                </span>
            </div>
            <div>
                <button v-on:click="state.settings_modal_active = true" class="inline-flex items-center justify-center text-xs font-medium text-gray-500 hover:text-gray-700 mr-6">
                    <SettingsIconFill class="h-4 w-4" />
                    <span class="ml-1">Settings</span>
                </button>
                <button v-on:click="state.delete_modal_active = true" class="inline-flex items-center justify-center text-xs font-medium text-gray-500 hover:text-gray-700">
                    <TrashIconFill class="h-4 w-4" />
                    <span class="ml-1">Delete</span>
                </button>
            </div>
        </div>
    </div>

    <Modal v-on:close="state.delete_modal_active = false" :active="state.delete_modal_active" size="sm">
        <div>
            <div class="mt-3 text-center sm:mt-5">
                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">Confirm Delete</DialogTitle>
                <div class="my-2">
                    <p class="text-sm text-gray-500">Are you sure you want to delete this file?</p>
                </div>
            </div>
        </div>
        <div class="mt-5 sm:mt-6">
            <Button v-on:click="deleteMedia()" type="button" color="red" class="mb-2">
                <span v-if="!state.delete_in_progress">Delete File</span>
                <LoadingIcon v-if="state.delete_in_progress" class="-ml-1 mr-3 h-5 w-5 animate-reverse-spin" />
            </Button>
            <Button v-if="true" type="button" color="blue-outline" v-on:click="state.delete_modal_active = false" :disabled="state.delete_in_progress">Cancel</Button>
        </div>    
    </Modal>

    <Modal v-on:close="state.settings_modal_active = false" :active="state.settings_modal_active" size="lg">
        <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">File Settings</DialogTitle>
        <Message :active="state.save_error" :heading="state.save_error_message" type="error" class="mt-4" />
        <div class="mt-5 sm:mt-6">
            <FormField v-for="(field, key) in form.media.fields" :key="key" :field="field" :namespace="`edit_media_${media.id}`" :name="key" class="mb-4" />
        </div>
        <div class="mt-5 sm:mt-6">
            <Button v-on:click="updateMedia()" type="button" color="blue" class="mb-2 sm:mb-0 sm:mr-2" width="auto">
                <span v-if="!state.settings_save_in_progress">Save</span>
                <LoadingIcon v-if="state.settings_save_in_progress" class="-ml-1 mr-3 h-5 w-5 animate-reverse-spin" />
            </Button>
            <Button v-if="true" type="button" width="auto" color="blue-outline" v-on:click="state.settings_modal_active = false" :disabled="state.settings_save_in_progress">Cancel</Button>
        </div> 
    </Modal>
</template>

<script>
import * as AssetService from '@ser/asset'
import { useAssetStore } from '@sto/asset.store'
import { isValid, getPayload } from '@lib/helpers'
import { reactive, watch } from 'vue'

import TrashIconFill from '@ico/TrashIconFill.vue'
import SettingsIconFill from '@ico/SettingsIconFill.vue'
import LoadingIcon from '@ico/LoadingIcon.vue'
import DragDropIcon from "@ico/DragDropIcon.vue"
//import StarOutlineIcon from '@ico/StarOutlineIcon.vue'
import StarSolidIcon from '@ico/StarSolidIcon.vue'

export default {
    components: {
        TrashIconFill,
        SettingsIconFill,
        LoadingIcon,
        //StarOutlineIcon,
        StarSolidIcon,
        DragDropIcon
    },
    props: ['asset_id', 'media'],
    setup(props, {emit}) {

        const ASSET_STORE = useAssetStore()
        
        const state = reactive({
            save_error: false,
            save_error_message: '',
            delete_modal_active: false,
            delete_in_progress: false,
            settings_modal_active: false,
            settings_save_in_progress: false
        })

        const form = reactive({
            media: {
                fields: {
                    caption: {
                        active: true,
                        label: 'Caption',
                        type: 'textarea',
                        value: props.media?.caption || '',
                        stored_value: props.media?.caption || '',
                        required: false,
                        pristine: props.media?.caption ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    visibility: {
                        active: true,
                        label: 'File visibility',
                        type: 'select',
                        value: props.media?.visibility || 'public',
                        stored_value: props.media?.visibility || '',
                        options: [
                            {
                                value: 'public',
                                label: 'Public: anyone can see the file'
                            },
                            {
                                value: 'private',
                                label: 'Private: only your account'
                            }
                        ],
                        required: false,
                        pristine: props.media?.visibility ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    primary: {
                        active: props.media.type == 'image',
                        label: 'Primary image',
                        type: 'toggle',
                        value: props.media?.primary || false,
                        stored_value: props.media?.caption || false,
                        required: false,
                        pristine: props.media?.caption ? false : true,
                        valid: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            }
        })

        // delete_modal_active: false,
        // delete_in_progress: false,

        const deleteMedia = async () => {

            state.delete_in_progress = true

            try {
                await AssetService.deleteMedia(props.media.id, props.asset_id)
                // insruct the parent to refresh the data (maintin a single source of truth for data)
                emit('refresh')

                state.delete_modal_active = false
                state.delete_in_progress = false

            } catch (error) {
                console.error('AssetService.deleteMedia error', error)
                state.delete_in_progress = false
            }
        }

        const updateMedia = async () => {

            // clear existing errors
            state.save_error_message = ''
            state.save_error = false

            state.settings_save_in_progress = true

            const payload = getPayload(form.media.fields)

            try {
                await AssetService.updateMedia(props.media.id, props.asset_id, payload)
                // insruct the parent to refresh the data (maintin a single source of truth for data)
                emit('refresh')

                state.settings_modal_active = false
                state.settings_save_in_progress = false

            } catch (error) {
                console.error('AssetService.updateMedia error', error)
                state.settings_save_in_progress = false
                state.save_error_message = 'Sorry, there was an error saving your changes'
                state.save_error = true
            }
        }

        const makePrimary = async () => {
            form.media.fields.primary.value = true
            await AssetService.updateMedia(props.media.id, props.asset_id, { primary: true })
            emit('refresh')
        }

        watch(
            () => props.media?.primary,
            (new_value, old_value) => {
                form.media.fields.primary.value = new_value ? true : false
            }
        )

        watch(
            () => form.media.fields.primary.value,
            (new_value, old_value) => {
                // if this is the primary image, it must be public
                if (new_value) form.media.fields.visibility.value = 'public'
            }
        )

        watch(
            () => form.media.fields.visibility.value,
            (new_value, old_value) => {
                // if this is the primary image, it must be public
                if (new_value != 'public' && form.media.fields.primary.value) form.media.fields.primary.value = false
            }
        )

        return {
            state,
            form,
            deleteMedia,
            updateMedia,
            makePrimary
        }
    },
}
</script>